/** TradingView-style chart (docs/11-charts.md): candles, trade markers, bot signal and position panes.
 *  Lightweight Charts (Apache-2.0) keeps its TradingView attribution logo. `replay` reveals bars up to a playhead. */
import { BaselineSeries, CandlestickSeries, ColorType, createChart, createSeriesMarkers, LineSeries, type IChartApi, type SeriesMarker, type UTCTimestamp } from "lightweight-charts";
import { useEffect, useMemo, useRef, useState } from "react";
import { coin, timeUtc, usd } from "../lib/format.ts";
import type { ChartData } from "../lib/types.ts";
import { Icon, ICONS, Segmented } from "./ui.tsx";

const css = (v: string, a = 1) => { const x = getComputedStyle(document.documentElement).getPropertyValue(`--${v}`).trim().split(/\s+/).join(","); return a === 1 ? `rgb(${x})` : `rgba(${x},${a})`; };
const ts = (t: number) => Math.floor(t / 1000) as UTCTimestamp;
/** Lightweight Charts needs strictly increasing times: sort and keep the last point per timestamp. */
function series<T extends { time: UTCTimestamp }>(pts: T[]): T[] {
  const m = new Map<number, T>();
  for (const p of pts) m.set(p.time, p);
  return [...m.values()].sort((a, b) => a.time - b.time);
}

export function PriceChart({ data, replay = false, height = 520, onSelect }: { data: ChartData; replay?: boolean; height?: number; onSelect?: (t: number) => void }) {
  const el = useRef<HTMLDivElement>(null);
  const chartRef = useRef<{ chart: IChartApi; set: (n: number) => void } | null>(null);
  const [play, setPlay] = useState(false), [speed, setSpeed] = useState<"1" | "5" | "20">("5");
  const [head, setHead] = useState(() => (replay ? Math.min(60, data.candles.length) : data.candles.length));
  const [hover, setHover] = useState<number | null>(null);
  const evAt = useMemo(() => new Map(data.events.map((e) => [Math.floor(e.t / 1000), e])), [data.events]);

  useEffect(() => {
    if (!el.current) return;
    const up = css("teal"), down = css("warn"), ink = css("muted"), line = css("line");
    const chart = createChart(el.current, {
      autoSize: true,
      layout: { background: { type: ColorType.Solid, color: "transparent" }, textColor: ink, fontFamily: "'Geist Variable', sans-serif", panes: { separatorColor: line } },
      grid: { vertLines: { color: css("line", 0.5) }, horzLines: { color: css("line", 0.5) } },
      timeScale: { timeVisible: true, borderColor: line }, rightPriceScale: { borderColor: line }, crosshair: { mode: 0 },
    });
    const candles = chart.addSeries(CandlestickSeries, { upColor: "rgba(0,0,0,0)", borderUpColor: up, wickUpColor: up, downColor: down, borderDownColor: down, wickDownColor: down });
    const signal = chart.addSeries(LineSeries, { color: css("accent"), lineWidth: 2, priceLineVisible: false, lastValueVisible: true, title: "Signal" }, 1);
    const pos = chart.addSeries(BaselineSeries, { baseValue: { type: "price", price: 0 }, topLineColor: css("accent"), topFillColor1: css("accent", 0.25), topFillColor2: css("accent", 0.05),
      bottomLineColor: down, bottomFillColor1: css("warn", 0.05), bottomFillColor2: css("warn", 0.25), lineType: 1, priceLineVisible: false, title: "Position" }, 2);
    chart.panes()[1]?.setHeight(80); chart.panes()[2]?.setHeight(80);
    const markers = createSeriesMarkers(candles, []);
    const set = (n: number) => {
      const c = data.candles.slice(0, n), tEnd = c.length ? c[c.length - 1]![0] + 1 : -Infinity;
      candles.setData(series(c.map(([t, o, h, l, cl]) => ({ time: ts(t), open: o, high: h, low: l, close: cl }))));
      signal.setData(series(data.signal.filter(([t]) => t < tEnd).map(([t, v]) => ({ time: ts(t), value: v }))));
      pos.setData(series(data.position.filter(([t]) => t < tEnd).map(([t, v]) => ({ time: ts(t), value: v }))));
      const ms: SeriesMarker<UTCTimestamp>[] = data.events.filter((e) => e.t < tEnd && (e.kind === "long" || e.kind === "short" || e.kind === "close")).map((e) => {
        const buy = e.side === "buy";
        return { time: ts(e.t), position: buy ? "belowBar" : "aboveBar", shape: buy ? "arrowUp" : "arrowDown", color: e.kind === "close" ? ink : e.kind === "long" ? css("accent") : down,
          text: e.kind === "close" ? "Close" : e.kind === "long" ? "Long" : "Short" };
      });
      // adds and trims: small dots, so the big moves stay readable
      for (const e of data.events) if (e.t < tEnd && (e.kind === "add" || e.kind === "trim")) ms.push({ time: ts(e.t), position: e.side === "buy" ? "belowBar" : "aboveBar", shape: "circle", size: 0.4, color: e.side === "buy" ? css("accent", 0.7) : css("warn", 0.7) });
      ms.sort((a, b) => a.time - b.time);
      markers.setMarkers(ms);
    };
    set(replay ? head : data.candles.length);
    chart.timeScale().fitContent();
    chart.subscribeCrosshairMove((p) => setHover(p.time ? Number(p.time) : null));
    chart.subscribeClick((p) => p.time && onSelect?.(Number(p.time) * 1000));
    chartRef.current = { chart, set };
    return () => { chart.remove(); chartRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => { if (replay) chartRef.current?.set(head); }, [head, replay]);
  useEffect(() => {
    if (!play) return;
    const iv = setInterval(() => setHead((h) => { if (h >= data.candles.length) { setPlay(false); return h; } return Math.min(h + Number(speed), data.candles.length); }), 120);
    return () => clearInterval(iv);
  }, [play, speed, data.candles.length]);

  const shown = hover ? data.candles.find((c) => Math.floor(c[0] / 1000) === hover) : data.candles[(replay ? head : data.candles.length) - 1];
  const ev = hover ? evAt.get(hover) : undefined;
  return (
    <div className="flex flex-col gap-2">
      {replay && (
        <div className="flex flex-wrap items-center gap-3">
          <button className="btn" onClick={() => { if (head >= data.candles.length) setHead(1); setPlay((p) => !p); }} aria-label={play ? "Pause replay" : "Play replay"}>
            <Icon d={play ? ICONS.pause : ICONS.play} size={15} />{play ? "Pause" : "Play"}
          </button>
          <Segmented label="Speed" value={speed} onChange={setSpeed} options={[["1", "1×"], ["5", "5×"], ["20", "20×"]]} />
          <input type="range" min={1} max={data.candles.length} value={head} onChange={(e) => { setPlay(false); setHead(Number(e.target.value)); }} className="min-w-[160px] flex-1 accent-[rgb(var(--accent))]" aria-label="Replay position" />
          <span className="num text-[13px] text-muted">{data.candles[head - 1] ? timeUtc(data.candles[head - 1]![0]) : ""}</span>
        </div>
      )}
      <div className="num flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-muted" aria-live="off">
        <span className="font-sans font-semibold text-ink-strong">{coin(data.symbol)} · {data.tf}</span>
        {shown && <><span>O {shown[1]}</span><span>H {shown[2]}</span><span>L {shown[3]}</span><span>C {shown[4]}</span></>}
        {ev && <span className={ev.side === "buy" ? "up" : "down"}>{ev.kind} {ev.side} {usd(ev.notional, 0)} @ {ev.price}</span>}
      </div>
      <div ref={el} style={{ height }} className="w-full" role="img" aria-label={`${coin(data.symbol)} price chart with ${data.events.length} bot trades; the trade list below has the same information`} />
    </div>
  );
}
